import { all, call, fork, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';

import * as authActions from 'actions/Auth';
import * as studentsActions from 'actions/Students';
import { showErrorNotification } from 'actions/App';
import * as studentApi from 'api/Students';
import {
  GET_STUDENT_RELATIVES,
  LOAD_MORE_PAST_STUDENTS_ABSENCES,
  SEARCH_PAST_STUDENT_ABSENCES,
  SEARCH_UPCOMING_STUDENT_ABSENCES,
  SEARCH_LOCATION_REQUESTS_HISTORY,
  SHOW_MORE_PAST_STUDENTS_ABSENCES,
  LOAD_MORE_LOCATION_REQUESTS,
  GET_INSURANCES,
  CREATE_ABSENCE,
  EDIT_ABSENCE,
  REMOVE_ABSENCE,
  STOP_ABSENCE,
  GET_STUDENT_LOCATION,
  LOAD_ABSENCE_REASONS,
} from 'constants/ActionTypes';
import { TYPES_ADDITIONALS, TYPES_PARENTS } from 'containers/AllUsers/constants';
import { getPupilContact } from 'api/Contacts';

const absencesSelector = state => state.students.absences.upcoming.data;
const currentUserProfileSelector = state => state.profile.user;

function* getSortedAbsenceArray(absence) {
  const absences = yield select(absencesSelector);

  if (!absences || !absences.length) {
    return [absence];
  }

  let isReplaced = false;
  // we should replace absence if we update it
  const arrayToSort = absences.map(abs => {
    if (abs.id !== absence.id) {
      return abs;
    }

    isReplaced = true;
    return absence;
  });

  if (!isReplaced) {
    // or add new in case of we add new absence
    arrayToSort.push(absence);
  }

  arrayToSort.sort((a, b) => new Date(b.fromTime) - new Date(a.fromTime));
  return arrayToSort;
}

/**
 * Fetches insurance info for specified user or current user if id hasn't been provided
 * @param payload
 * @returns {IterableIterator<*>}
 */
function* getInsurances({ payload }) {
  yield put(studentsActions.startLoading(true));
  let userId = payload;

  if (!userId) {
    const currentUserProfile = yield select(currentUserProfileSelector);
    // eslint-disable-next-line
    userId = currentUserProfile.userId;
  }

  try {
    const { data } = yield call(studentApi.getInsurances, userId);

    yield put(studentsActions.getInsurancesSuccess(data));
  } catch (error) {
    yield put(showErrorNotification(error.messages));
  }

  yield put(studentsActions.stopLoading(false));
}

function* createAbsence({ payload }) {
  const { studentId, request } = payload;

  yield put(authActions.showAuthLoader());
  try {
    const source = yield call(studentApi.createAbsence, studentId, request);
    yield put(studentsActions.createAbsenceSuccess(source.data));
    yield put(
      studentsActions.searchUpcomingStudentAbsencesSuccess(
        yield getSortedAbsenceArray(source.data),
        studentId,
      ),
    );
  } catch (error) {
    yield put(studentsActions.absencePendingFailure());
    yield put(showErrorNotification(error.messages));
  }
}

function* editAbsence({ payload }) {
  const { studentId, absenceId, absence } = payload;

  yield put(authActions.showAuthLoader());
  try {
    const source = yield call(studentApi.editAbsence, studentId, absenceId, absence);
    yield put(studentsActions.editAbsenceSuccess(source.data));
    yield put(
      studentsActions.searchUpcomingStudentAbsencesSuccess(
        yield getSortedAbsenceArray(source.data),
        studentId,
      ),
    );
  } catch (error) {
    yield put(studentsActions.absencePendingFailure());
    yield put(showErrorNotification(error.messages));
  }
}

function* removeAbsence({ payload }) {
  const { studentId, absenceId } = payload;

  yield put(authActions.showAuthLoader());
  try {
    yield call(studentApi.removeAbsence, studentId, absenceId);
    yield put(studentsActions.removeAbsenceSuccess(absenceId));
  } catch (error) {
    yield put(studentsActions.absencePendingFailure());
    yield put(showErrorNotification(error.messages));
  }
}

function* stopAbsence({ payload }) {
  const { studentId, absenceId } = payload;

  yield put(authActions.showAuthLoader());
  try {
    yield call(studentApi.stopAbsence, studentId, absenceId);
    yield put(studentsActions.stopAbsenceSuccess(absenceId));
  } catch (error) {
    yield put(studentsActions.absencePendingFailure());
    yield put(showErrorNotification(error.messages));
  }
}

function* getStudentLocation({ payload }) {
  yield put(studentsActions.setStudentLocationLoading(true));

  try {
    const { data } = yield call(studentApi.getStudentLocation, payload.studentId);

    yield put(studentsActions.getStudentLocationSuccess(data.location));
  } catch (error) {
    yield put(showErrorNotification(error.messages));
  }

  yield put(studentsActions.setStudentLocationLoading(false));
}

const getFilteredRelatives = contacts =>
  contacts.reduce(
    (acc, item) => {
      if (TYPES_PARENTS.includes(item.relationshipRaw)) {
        acc.parents.push(item);
      }
      if (TYPES_ADDITIONALS.find(({ value }) => value === item.relationshipRaw)) {
        acc.additionals.push(item);
      }
      return acc;
    },
    { parents: [], additionals: [] },
  );

function* getStudentRelatives({ payload: { studentId } }) {
  try {
    const {
      data: { contacts = [] },
    } = yield call(studentApi.getParents, studentId);
    const { parents, additionals } = getFilteredRelatives(contacts);

    const pupilContactsRequests = additionals.map(({ id }) => call(getPupilContact, id));

    const additionalContactsResponces = yield all(pupilContactsRequests);
    const additionalContacts = additionalContactsResponces.map(({ data }) => data);

    yield put(
      studentsActions.getStudentRelativesSuccess(
        { parents, additionals: additionalContacts },
        studentId,
      ),
    );
  } catch (error) {
    yield put(studentsActions.getStudentRelativesFailure());
    yield put(showErrorNotification(error.messages));
  }
}

function* loadMorePastStudentAbsences({ payload: { studentId, query } }) {
  try {
    const [absences] = yield all([
      call(studentApi.searchAbsences, studentId, query),
      take(SHOW_MORE_PAST_STUDENTS_ABSENCES),
    ]);
    yield put(
      studentsActions.loadMorePastStudentAbsencesSuccess(
        absences.data.entities,
        absences.data.pageCount,
        studentId,
      ),
    );
  } catch (error) {
    yield put(studentsActions.loadMorePastStudentAbsencesFailure());
    yield put(showErrorNotification(error.messages));
  }
}

function* searchPastStudentAbsences({ payload: { studentId, query } }) {
  try {
    const {
      data: { entities, pageCount },
    } = yield call(studentApi.searchAbsences, studentId, query);
    yield put(studentsActions.searchPastStudentAbsencesSuccess(entities, studentId, pageCount));
  } catch (error) {
    yield put(studentsActions.searchPastStudentAbsencesFailure());
    yield put(showErrorNotification(error.messages));
  }
}

function* searchUpcomingStudentAbsences({ payload: { studentId, query } }) {
  try {
    const {
      data: { entities, pageCount },
    } = yield call(studentApi.searchAbsences, studentId, query);
    yield put(studentsActions.searchUpcomingStudentAbsencesSuccess(entities, studentId, pageCount));
  } catch (error) {
    yield put(studentsActions.searchUpcomingStudentAbsencesFailure());
    yield put(showErrorNotification(error.messages));
  }
}

function* searchLocationRequestsHistory({ payload: { studentId, query } }) {
  try {
    const { data } = yield call(studentApi.searchLocationRequests, studentId, query);
    yield put(studentsActions.searchLocationRequestsHistorySuccess(data.entities, data.pageCount));
  } catch (error) {
    yield put(studentsActions.searchLocationRequestsHistoryFailure());
    yield put(showErrorNotification(error.messages));
  }
}

function* loadMoreLocationRequests({ payload: { studentId, query } }) {
  try {
    const { data } = yield call(studentApi.searchLocationRequests, studentId, query);
    yield put(studentsActions.loadMoreLocationRequestsSuccess(data.entities, data.pageCount));
  } catch (error) {
    yield put(studentsActions.loadMoreLocationRequestsFailure());
    yield put(showErrorNotification(error.messages));
  }
}

function* getAbsenceReasons() {
  try {
    const { data } = yield call(studentApi.getAbsenceReasons);
    yield put(studentsActions.loadAbsenceReasonsSuccess(data.entities));
  } catch (error) {
    yield put(showErrorNotification(error.messages));
  }
}

export function* getStudentRelativesSaga() {
  yield takeLatest(GET_STUDENT_RELATIVES, getStudentRelatives);
}
export function* loadMorePastStudentAbsencesSaga() {
  yield takeLatest(LOAD_MORE_PAST_STUDENTS_ABSENCES, loadMorePastStudentAbsences);
}
export function* searchPastStudentAbsencesSaga() {
  yield takeLatest(SEARCH_PAST_STUDENT_ABSENCES, searchPastStudentAbsences);
}
export function* searchUpcomingStudentAbsencesSaga() {
  yield takeLatest(SEARCH_UPCOMING_STUDENT_ABSENCES, searchUpcomingStudentAbsences);
}
export function* searchLocationRequestsHistorySaga() {
  yield takeLatest(SEARCH_LOCATION_REQUESTS_HISTORY, searchLocationRequestsHistory);
}
export function* loadMoreLocationRequestsSaga() {
  yield takeLatest(LOAD_MORE_LOCATION_REQUESTS, loadMoreLocationRequests);
}
export function* getInsurancesByStudent() {
  yield takeEvery(GET_INSURANCES, getInsurances);
}
export function* createNewAbsence() {
  yield takeEvery(CREATE_ABSENCE, createAbsence);
}
export function* updateAbsence() {
  yield takeEvery(EDIT_ABSENCE, editAbsence);
}
export function* deleteAbsence() {
  yield takeEvery(REMOVE_ABSENCE, removeAbsence);
}
export function* stopAbsenceSaga() {
  yield takeEvery(STOP_ABSENCE, stopAbsence);
}
export function* getStudentLocationSaga() {
  yield takeEvery(GET_STUDENT_LOCATION, getStudentLocation);
}

export function* getAbsenceReasonsSaga() {
  yield takeLatest(LOAD_ABSENCE_REASONS, getAbsenceReasons);
}

export default function* rootSaga() {
  yield all([
    fork(getStudentRelativesSaga),
    fork(loadMorePastStudentAbsencesSaga),
    fork(searchPastStudentAbsencesSaga),
    fork(searchUpcomingStudentAbsencesSaga),
    fork(searchLocationRequestsHistorySaga),
    fork(loadMoreLocationRequestsSaga),
    fork(getInsurancesByStudent),
    fork(createNewAbsence),
    fork(updateAbsence),
    fork(deleteAbsence),
    fork(stopAbsenceSaga),
    fork(getStudentLocationSaga),
    fork(getAbsenceReasonsSaga),
  ]);
}
